import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Check,
  Sparkles,
  ArrowLeft,
  Loader2,
  ShieldCheck,
  Zap,
  Wifi,
  FileSignature,
  Truck,
  Upload,
  FileSpreadsheet,
  Camera,
  Users,
  Crown,
} from 'lucide-react';
import { billingApi } from '../lib/billingApi';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { isInstalledApp, isCapacitorIOS } from '../lib/platform';
import { startCheckout as runCheckout, restorePurchases, initBilling } from '../lib/paymentService';
import { applyApplePricing, buildNativeIosFallbackConfig } from '../lib/revenueCatProducts';
// Local bilingual dictionary — keeps this page self-contained without
// polluting the global i18n catalog with 80+ marketing strings.
const STR = {
  fr: {
    back:                    'Retour',
    home:                    'Accueil',
    login:                   'Connexion',
    hero_badge:              'ESSAI GRATUIT — 3 JOURS · SANS CARTE',
    hero_title:              "L'outil des entrepreneurs sérieux du Canada",
    hero_subtitle:           "Crée, envoie et fais signer des soumissions professionnelles en moins de 10 minutes. Du chantier. Même hors ligne.",
    highlights: [
      { Icon: Zap,           label: 'Soumission pro en 10 min' },
      { Icon: Wifi,          label: 'Marche hors ligne' },
      { Icon: FileSignature, label: 'Signature légale incluse' },
      { Icon: ShieldCheck,   label: 'Paiement sécurisé Stripe' },
    ],
    owner_free_banner:       '👑 Membre fondateur — Accès illimité à vie. Aucun abonnement requis.',
    active_banner:           '✓ Tu as déjà un abonnement actif. Gère-le depuis ton profil.',
    active_banner_ios:       '✓ Tu as déjà un abonnement actif. Gère-le dans Réglages iOS → Identifiant Apple → Abonnements.',
    auto_trial_banner:       (n) => `⏳ Essai gratuit en cours — ${n === 1 ? '1 jour restant' : `${n} jours restants`}. Choisis ton plan avant la fin pour continuer.`,
    installed_app_title:     "Pour t'abonner, visite notre site web",
    installed_app_desc_pre:  "L'abonnement s'active sur ",
    installed_app_desc_post: " dans ton navigateur. Une fois activé, il sera disponible dans l'app.",
    weekly_eyebrow:          'ESSAI HEBDOMADAIRE · 1 UTILISATEUR',
    weekly_title:            'Découverte',
    weekly_desc:             "Idéal pour tester l'app ou pour un usage ponctuel.",
    per_week_suffix:         '$ CAD / sem',
    taxes_excl:              'taxes en sus',
    sub_eyebrow:             'ABONNEMENT MULTI-UTILISATEURS',
    sub_title:               'Choisis ton plan',
    toggle_monthly:          'Mensuel',
    toggle_annual:           'Annuel',
    save_badge:              '−20%',
    most_popular:            '⭐ LE PLUS POPULAIRE',
    per_month_suffix:        '$/mois',
    billed_annually:         (amt) => `Facturé ${amt} $ par année, taxes en sus`,
    billed_monthly:          'Facturé mensuellement, taxes en sus',
    savings_label:           (amt) => `💰 Économise ${amt} $/an`,
    seats_one:               '1 utilisateur',
    seats_up_to:             (n) => `Jusqu'à ${n} utilisateurs`,
    cta_visit_site:          'Visiter soumissionpro.net',
    cta_start_trial:         'Commencer mon essai de 3 jours',
    cta_subscribe:           'S\'abonner maintenant',
    cta_create_account:      'Créer mon compte gratuit',
    cta_redirecting:         'Redirection…',
    checkout_error:          'Erreur lors de la création de la session de paiement',
    store_only_title:        'Abonnements non disponibles dans l\'app',
    store_only_desc:         "Pour des raisons de politique de la plateforme, les abonnements ne sont pas accessibles depuis cette app. Si vous avez déjà un abonnement actif, votre accès reste fonctionnel.",
    store_only_features_title: 'Tout reste accessible dans l\'app',
    store_only_features: [
      'Soumissions illimitées sur place',
      'Catalogue de 1500+ matériaux',
      'Mode hors ligne complet',
      'Signature électronique',
      'Génération PDF premium',
      'Photos de chantier',
    ],
    tier_solo_label:         'Solo',
    tier_solo_desc:          'Pour les entrepreneurs autonomes.',
    tier_team_label:         'Équipe',
    tier_team_desc:          "Jusqu'à 5 utilisateurs partagent un compte.",
    tier_pro_label:          'Pro Illimité',
    tier_pro_desc:           "Jusqu'à 10 utilisateurs. Pour les entreprises sérieuses.",
    game_eyebrow:            "GAME CHANGER · INCLUS DANS L'ABONNEMENT",
    game_title_pre:          'Importe les prix de tes fournisseurs.',
    game_title_emph:         "L'IA fait le reste.",
    game_desc_pre:           "Tu as négocié des prix spéciaux avec BMR, Réno-Dépôt, Patrick Morin? Upload leur liste (Excel, PDF, ou prends une photo!) et utilise ",
    game_desc_emph:          "TES vrais prix",
    game_desc_post:          " dans tes soumissions.",
    game_excel_title:        'Excel / CSV',
    game_excel_desc:         "Glisse le fichier de ton fournisseur. Extraction instantanée.",
    game_pdf_title:          'PDF',
    game_pdf_desc:           "Catalogue, soumission, facture — l'IA lit tout.",
    game_photo_title:        'Photo du téléphone',
    game_photo_desc:         "Liste papier? Prends-la en photo, c'est tout.",
    game_footer:             "Inclus dans tous les plans — accessible aussi pendant l'essai gratuit de 3 jours.",
    faq_title:               'Questions fréquentes',
    footer:                  'Paiement sécurisé par Stripe · Annule à tout moment · Support : support@soumissionpro.net',
    restore_purchases:       'Restaurer mes achats',
    restoring:               'Restauration…',
    ios_pricing_note:        'Les abonnements sont gérés par Apple. Annule à tout moment depuis les Réglages iOS.',
    subscription_terms_title: "Conditions d'abonnement",
    subscription_terms_bullets: [
      "Abonnement auto-renouvelable. Se renouvelle automatiquement à la fin de chaque période, sauf annulation au moins 24 heures avant la fin de la période en cours.",
      "Le paiement est porté à ton identifiant Apple à la confirmation de l'achat.",
      "Le montant du renouvellement est identique au prix affiché ci-dessus.",
      "Gère ou annule ton abonnement à tout moment dans Réglages iOS → Identifiant Apple → Abonnements.",
    ],
    legal_links_intro:       'En t\'abonnant, tu acceptes nos:',
    privacy_policy_link:     'Politique de confidentialité',
    terms_of_use_link:       "Conditions d'utilisation (EULA)",
    features: [
      'Soumissions illimitées',
      'Catalogue de 1500+ matériaux canadiens',
      'Ajustement de prix par intelligence artificielle',
      'Import des prix de tes fournisseurs (Excel, PDF, photo)',
      'Calculateur de métré intégré',
      'Templates de soumissions réutilisables',
      'PDF premium avec logo et badge licence vérifiée',
      'Signature électronique sur place et à distance',
      'Mode hors ligne complet',
      'Synchronisation multi-appareils',
      'Photos de chantier et suivi des dépenses',
      'Calcul automatique TPS, TVQ, TVH, PST',
      'Support en français et anglais',
    ],
    faqs: [
      { q: "L'essai gratuit est-il vraiment gratuit ?",
        a: "Oui. Aucune carte de crédit n'est requise pour démarrer ton essai de 3 jours. Tu accèdes à toutes les fonctionnalités premium sans engagement." },
      { q: 'Que se passe-t-il après les 3 jours ?',
        a: "Tu ajoutes ta carte pour continuer à utiliser SoumissionPro. Si tu ne le fais pas, ton abonnement s'arrête automatiquement, sans frais." },
      { q: "C'est quoi un plan Équipe ?",
        a: "Le plan Équipe permet à jusqu'à 5 utilisateurs de partager les soumissions, clients et fournisseurs de ta compagnie. Le plan Pro Illimité monte jusqu'à 10 utilisateurs." },
      { q: "Puis-je annuler à tout moment ?",
        a: 'Absolument. Tu peux annuler ton abonnement en deux clics depuis ton profil. Aucune question. Aucune pénalité.' },
    ],
  },
  en: {
    back:                    'Back',
    home:                    'Home',
    login:                   'Sign in',
    hero_badge:              'FREE TRIAL — 3 DAYS · NO CARD',
    hero_title:              "The tool for serious Canadian contractors",
    hero_subtitle:           "Create, send and get quotes signed in under 10 minutes — from the job site, even offline.",
    highlights: [
      { Icon: Zap,           label: 'Pro quote in 10 min' },
      { Icon: Wifi,          label: 'Works offline' },
      { Icon: FileSignature, label: 'Legal signature included' },
      { Icon: ShieldCheck,   label: 'Secure Stripe payment' },
    ],
    owner_free_banner:       '👑 Founding member — Unlimited lifetime access. No subscription required.',
    active_banner:           '✓ You already have an active subscription. Manage it from your profile.',
    active_banner_ios:       '✓ You already have an active subscription. Manage it in iOS Settings → Apple ID → Subscriptions.',
    auto_trial_banner:       (n) => `⏳ Free trial in progress — ${n === 1 ? '1 day remaining' : `${n} days remaining`}. Choose your plan before it ends to continue.`,
    installed_app_title:     'To subscribe, visit our website',
    installed_app_desc_pre:  'Subscriptions are activated on ',
    installed_app_desc_post: ' in your browser. Once activated, it will be available in the app.',
    weekly_eyebrow:          'WEEKLY TRIAL · 1 USER',
    weekly_title:            'Discovery',
    weekly_desc:             'Ideal for testing the app or for occasional use.',
    per_week_suffix:         '$ CAD / wk',
    taxes_excl:              'taxes extra',
    sub_eyebrow:             'MULTI-USER SUBSCRIPTION',
    sub_title:               'Choose your plan',
    toggle_monthly:          'Monthly',
    toggle_annual:           'Annual',
    save_badge:              '−20%',
    most_popular:            '⭐ MOST POPULAR',
    per_month_suffix:        '$/month',
    billed_annually:         (amt) => `Billed ${amt} $ per year, taxes extra`,
    billed_monthly:          'Billed monthly, taxes extra',
    savings_label:           (amt) => `💰 Save ${amt} $/year`,
    seats_one:               '1 user',
    seats_up_to:             (n) => `Up to ${n} users`,
    cta_visit_site:          'Visit soumissionpro.net',
    cta_start_trial:         'Start my 3-day trial',
    cta_subscribe:           'Subscribe now',
    cta_create_account:      'Create my free account',
    cta_redirecting:         'Redirecting…',
    checkout_error:          'Error creating the payment session',
    store_only_title:        'Subscriptions are not available in this app',
    store_only_desc:         'Due to platform policy, subscriptions are not accessible from this app. If you already have an active subscription, your access remains functional.',
    store_only_features_title: 'Everything stays accessible in the app',
    store_only_features: [
      'Unlimited on-site quotes',
      'Catalog of 1500+ materials',
      'Full offline mode',
      'Electronic signature',
      'Premium PDF generation',
      'Job site photos',
    ],
    tier_solo_label:         'Solo',
    tier_solo_desc:          'For independent contractors.',
    tier_team_label:         'Team',
    tier_team_desc:          'Up to 5 users share one account.',
    tier_pro_label:          'Unlimited Pro',
    tier_pro_desc:           'Up to 10 users. For serious businesses.',
    game_eyebrow:            'GAME CHANGER · INCLUDED WITH SUBSCRIPTION',
    game_title_pre:          "Import your suppliers' prices.",
    game_title_emph:         'AI does the rest.',
    game_desc_pre:           "Negotiated special pricing with BMR, Réno-Dépôt, Patrick Morin? Upload their list (Excel, PDF, or snap a photo!) and use ",
    game_desc_emph:          "YOUR real prices",
    game_desc_post:          " in your quotes.",
    game_excel_title:        'Excel / CSV',
    game_excel_desc:         "Drop your supplier's file. Instant extraction.",
    game_pdf_title:          'PDF',
    game_pdf_desc:           'Catalog, quote, invoice — AI reads it all.',
    game_photo_title:        'Phone photo',
    game_photo_desc:         "Paper list? Just snap a photo — that's it.",
    game_footer:             'Included in all plans — also accessible during the 3-day free trial.',
    faq_title:               'Frequently asked questions',
    footer:                  'Secure payment by Stripe · Cancel anytime · Support: support@soumissionpro.net',
    restore_purchases:       'Restore my purchases',
    restoring:               'Restoring…',
    ios_pricing_note:        'Subscriptions are managed by Apple. Cancel anytime from your iOS Settings.',
    subscription_terms_title: 'Subscription Terms',
    subscription_terms_bullets: [
      'Auto-renewable subscription. Automatically renews at the end of each period unless canceled at least 24 hours before the end of the current period.',
      'Payment will be charged to your Apple ID account upon purchase confirmation.',
      'Renewal price is identical to the price shown above.',
      'Manage or cancel your subscription anytime in iOS Settings → Apple ID → Subscriptions.',
    ],
    legal_links_intro:       'By subscribing, you agree to our:',
    privacy_policy_link:     'Privacy Policy',
    terms_of_use_link:       'Terms of Use (EULA)',
    features: [
      'Unlimited quotes',
      'Catalog of 1500+ Canadian materials',
      'AI-powered price adjustment',
      "Import your suppliers' prices (Excel, PDF, photo)",
      'Built-in measurement calculator',
      'Reusable quote templates',
      'Premium PDF with logo and verified licence badge',
      'Electronic signature on-site and remote',
      'Full offline mode',
      'Multi-device sync',
      'Job site photos and expense tracking',
      'Automatic GST, QST, HST, PST calculation',
      'Support in French and English',
    ],
    faqs: [
      { q: 'Is the free trial really free?',
        a: 'Yes. No credit card required to start your 3-day trial. You get access to all premium features with no commitment.' },
      { q: 'What happens after the 3 days?',
        a: "You add your card to keep using SoumissionPro. If you don't, your subscription stops automatically, with no charge." },
      { q: 'What is a Team plan?',
        a: 'The Team plan allows up to 5 users to share your company quotes, clients and suppliers. The Unlimited Pro plan supports up to 10 users.' },
      { q: 'Can I cancel anytime?',
        a: 'Absolutely. Cancel your subscription in two clicks from your profile. No questions. No penalty.' },
    ],
  },
};

function daysLeft(isoDate) {
  if (!isoDate) return null;
  const ms = new Date(isoDate).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
}

// Tier metadata that doesn't need translation (icon + cache keys).
const TIER_META = [
  { tier: 'solo', icon: Sparkles, monthlyKey: 'monthly_solo', annualKey: 'annual_solo', seats: 1,  accent: false },
  { tier: 'team', icon: Users,    monthlyKey: 'monthly_team', annualKey: 'annual_team', seats: 5,  accent: true  },
  { tier: 'pro',  icon: Crown,    monthlyKey: 'monthly_pro',  annualKey: 'annual_pro',  seats: 10, accent: false },
];

function fmtPrice(amount) {
  if (amount == null) return '—';
  const fixed = Number(amount).toFixed(2);
  return fixed.replace('.', ',');
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [loading, setLoading] = useState(null);
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('annual'); // 'monthly' | 'annual'
  const installedApp = isInstalledApp();
  const nativeIOS = isCapacitorIOS();

  useEffect(() => {
    // Affichage immédiat sur iOS natif — ne jamais laisser /pricing vide si
    // le backend est lent ou indisponible (rejet Guideline 2.1a sur iPad).
    if (nativeIOS) {
      setConfig(buildNativeIosFallbackConfig());
    }
    billingApi.config()
      .then((cfg) => setConfig(nativeIOS ? applyApplePricing(cfg) : cfg))
      .catch(() => {
        if (nativeIOS) setConfig(buildNativeIosFallbackConfig());
      });
    if (user) billingApi.status().then(setStatus).catch(() => {});
    // Configure RevenueCat for the current Supabase user. No-op on web.
    if (user?.id) initBilling({ userId: user.id }).catch(() => {});
  }, [user, nativeIOS]);

  const startCheckout = async (planKey) => {
    // Legacy PWA (no IAP available) → redirect to website
    if (installedApp) {
      window.open('https://soumissionpro.net/pricing', '_blank', 'noopener');
      return;
    }
    if (!user) {
      navigate('/auth?next=/pricing');
      return;
    }
    setLoading(planKey);
    try {
      // paymentService routes Stripe (web) vs Apple IAP (Capacitor iOS) automatically.
      const result = await runCheckout({ planKey });
      if (result?.ok) {
        if (nativeIOS) {
          // 🍎 STRATÉGIE ULTRA-RAPIDE — On NE attend PAS le backend.
          // Apple a confirmé l'achat (result.ok=true + customerInfo). C'est
          // la SEULE source de vérité légale. Le webhook RevenueCat→Supabase
          // peut prendre 0-60 secondes en sandbox/prod — l'utilisateur ne
          // doit PAS attendre. On force le cache local en "active" et on
          // redirige instantanément. Le backend se synchronisera tout seul
          // dans la minute qui suit, et le prochain status() le confirmera.
          const customerInfo = result.customerInfo;
          const hasActiveEntitlement = customerInfo?.entitlements?.active &&
            Object.keys(customerInfo.entitlements.active).length > 0;
          if (hasActiveEntitlement) {
            // Écriture directe du cache au format SubscriptionGate
            try {
              const fakeStatus = {
                status: 'active',
                plan: planKey,
                source: 'apple_iap',
                auto_trial: false,
                auto_trial_expired: false,
              };
              localStorage.setItem('sp.lastSubStatus.v1', JSON.stringify({
                status: fakeStatus,
                ts: Date.now(),
              }));
              // 🔒 GRACE PERIOD IAP — pendant 5 minutes, SubscriptionGate
              // fait confiance au cache et N'APPELLE PAS le backend (qui
              // pourrait encore dire "trial expired" car webhook pas synchro).
              // C'est CRITIQUE: sans ça, le paywall réapparaît après navigation.
              localStorage.setItem('sp.iapGraceUntil.v1', String(Date.now() + 5 * 60 * 1000));
              // 🍎 Flag PERSISTANT — une fois posé, SubscriptionGate ne
              // réaffichera JAMAIS "essai gratuit terminé" même si le backend
              // tarde. À la place: écran neutre de synchronisation + refresh.
              localStorage.setItem('sp.iapPurchased.v1', String(Date.now()));
              setStatus(fakeStatus);
            } catch { /* localStorage disabled */ }
            toast.success(lang === 'en' ? 'Subscription activated!' : 'Abonnement activé !');
            // Dispatch + navigate IMMÉDIAT (zéro attente backend)
            window.dispatchEvent(new CustomEvent('subscription:refresh'));
            // Petit délai (300ms) pour que le toast s'affiche
            setTimeout(() => { navigate('/', { replace: true }); }, 300);
            // En arrière-plan, on déclenche un sync backend (sans bloquer)
            setTimeout(() => {
              billingApi.status().catch(() => { /* webhook pas encore arrivé, pas grave */ });
            }, 5000);
            return;
          }
          // Cas rare: pas d'entitlement actif → on retombe sur le polling court
          toast.success(lang === 'en' ? 'Subscription activated!' : 'Abonnement activé !');
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const fresh = await billingApi.status();
            setStatus(fresh);
          } catch { /* ignore */ }
          window.dispatchEvent(new CustomEvent('subscription:refresh'));
          navigate('/', { replace: true });
          return;
        }
      } else if (result?.userCancelled) {
        // User cancelled in the Apple sheet — no error toast needed.
      } else if (result?.error) {
        toast.error(result.error);
      }
    } catch (e) {
      toast.error(e.message || T.checkout_error);
    } finally {
      setLoading(null);
    }
  };

  const handleRestore = async () => {
    setLoading('__restore__');
    try {
      const result = await restorePurchases();
      if (result?.ok) {
        toast.success(lang === 'en' ? 'Purchases restored.' : 'Achats restaurés.');
        // 🍎 Pareil que purchase: trust customerInfo + navigate INSTANTANÉ
        if (nativeIOS) {
          const customerInfo = result.customerInfo;
          const hasActiveEntitlement = customerInfo?.entitlements?.active &&
            Object.keys(customerInfo.entitlements.active).length > 0;
          if (hasActiveEntitlement) {
            try {
              const fakeStatus = {
                status: 'active',
                source: 'apple_iap_restore',
                auto_trial: false,
                auto_trial_expired: false,
              };
              localStorage.setItem('sp.lastSubStatus.v1', JSON.stringify({
                status: fakeStatus,
                ts: Date.now(),
              }));
              localStorage.setItem('sp.iapGraceUntil.v1', String(Date.now() + 5 * 60 * 1000));
              // 🍎 Flag persistant: restore = preuve qu'un achat valide existe
              // sur le compte Apple → on ne montrera plus jamais "essai terminé".
              localStorage.setItem('sp.iapPurchased.v1', String(Date.now()));
              setStatus(fakeStatus);
            } catch { /* ignore */ }
          }
          window.dispatchEvent(new CustomEvent('subscription:refresh'));
          setTimeout(() => { navigate('/', { replace: true }); }, 300);
          return;
        }
        try {
          const fresh = await billingApi.status();
          setStatus(fresh);
        } catch { /* ignore */ }
      } else if (result?.error) {
        toast.error(result.error);
      }
    } finally {
      setLoading(null);
    }
  };

  const isOwnerFree = status?.plan === 'owner_free';
  const isAutoTrial = status?.auto_trial === true;
  const isPaidSubscription =
    status &&
    !isOwnerFree &&
    !isAutoTrial &&
    ['active', 'trialing'].includes(status.status);
  const isActive = isOwnerFree || isPaidSubscription;
  const trialDaysLeft = isAutoTrial ? daysLeft(status?.trial_end) : null;

  // Map raw tier metadata + translated labels/descriptions.
  const tierLabels = {
    solo: { label: T.tier_solo_label, description: T.tier_solo_desc },
    team: { label: T.tier_team_label, description: T.tier_team_desc },
    pro:  { label: T.tier_pro_label,  description: T.tier_pro_desc  },
  };

  const tiers = useMemo(() => {
    if (!config?.plans) return [];
    return TIER_META.map((t) => {
      const monthly = config.plans[t.monthlyKey];
      const annual = config.plans[t.annualKey];
      return { ...t, ...tierLabels[t.tier], monthly, annual };
    });
  }, [config, lang, tierLabels]);

  const weeklyPlan = config?.plans?.weekly_solo;

  const ctaLabel = (planKey) => {
    if (loading === planKey) return null;
    if (installedApp) return T.cta_visit_site;
    if (user) {
      // 🍎 Apple Guideline 3.1.2: ne JAMAIS afficher "Commencer mon essai de
      // 3 jours" si l'utilisateur a déjà consommé son essai gratuit (sinon
      // = fausse promesse = motif de rejet). On switch dynamiquement:
      //  - Aucun essai entamé encore        → "Commencer essai 3 jours"
      //  - Essai en cours OU déjà expiré    → "S'abonner maintenant"
      // Note: auto_trial_expired et auto_trial sont des FLAGS séparés du
      // champ status (status est l'état de la subscription Stripe/IAP).
      const trialAlreadyConsumed = status?.auto_trial === true || status?.auto_trial_expired === true || !!status?.trial_end;
      return trialAlreadyConsumed ? T.cta_subscribe : T.cta_start_trial;
    }
    return T.cta_create_account;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF7F2' }}>
      <header
        className="px-4 sm:px-8 py-4 flex items-center justify-between border-b pt-safe"
        style={{ borderColor: '#E5DCC9' }}
      >
        {user ? (
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium"
            style={{ color: '#7A1F2B' }}
            data-testid="pricing-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {T.back}
          </button>
        ) : (
          <a
            href="https://soumissionpro.net"
            className="inline-flex items-center gap-2 text-sm font-medium"
            style={{ color: '#7A1F2B' }}
          >
            <ArrowLeft className="w-4 h-4" /> {T.home}
          </a>
        )}
        <div className="font-bold text-sm tracking-widest" style={{ color: '#7A1F2B' }}>
          SOUMISSIONPRO
        </div>
        {user ? (
          <div className="w-16" />
        ) : (
          <button
            onClick={() => navigate('/auth')}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg"
            style={{ color: '#7A1F2B' }}
            data-testid="pricing-login-btn"
          >
            {T.login}
          </button>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest mb-3"
            style={{ color: '#C9A961' }}
          >
            <Sparkles className="w-3.5 h-3.5" /> {T.hero_badge}
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-3" style={{ color: '#5A1620' }}>
            {T.hero_title}
          </h1>
          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#4A5568' }}>
            {T.hero_subtitle}
          </p>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-sm">
            {T.highlights
              .filter((h) => !nativeIOS || !/stripe/i.test(h.label))
              .map((h) => (
              <div
                key={h.label}
                className="inline-flex items-center gap-1.5"
                style={{ color: '#5A1620' }}
              >
                <h.Icon className="w-4 h-4" style={{ color: '#C9A961' }} />
                <span className="font-medium">{h.label}</span>
              </div>
            ))}
          </div>
        </div>

        {isOwnerFree && (
          <div
            className="mb-8 p-4 rounded-xl text-center font-medium"
            style={{ backgroundColor: '#DCFCE7', color: '#166534', border: '1px solid #16A34A33' }}
            data-testid="pricing-owner-free-banner"
          >
            {T.owner_free_banner}
          </div>
        )}

        {isPaidSubscription && (
          <div
            className="mb-8 p-4 rounded-xl text-center font-medium"
            style={{ backgroundColor: '#E5DCC9', color: '#5A1620' }}
            data-testid="pricing-active-banner"
          >
            {nativeIOS ? T.active_banner_ios : T.active_banner}
          </div>
        )}

        {isAutoTrial && (
          <div
            className="mb-8 p-4 rounded-xl text-center font-medium"
            style={{ backgroundColor: '#FAF7F2', color: '#5A1620', border: '1px solid #C9A961' }}
            data-testid="pricing-auto-trial-banner"
          >
            {T.auto_trial_banner(trialDaysLeft || 0)}
          </div>
        )}

        {installedApp && !isActive && (
          <div
            className="mb-8 p-6 rounded-2xl border-2"
            style={{ backgroundColor: '#FAF7F2', borderColor: '#C9A961', color: '#5A1620' }}
            data-testid="pricing-installed-app-notice"
          >
            <div className="text-center">
              <div className="font-bold text-xl mb-2" style={{ color: '#5A1620' }}>
                {T.store_only_title}
              </div>
              <div className="text-sm leading-relaxed mb-5" style={{ color: '#4A5568' }}>
                {T.store_only_desc}
              </div>
              <div
                className="font-bold text-sm tracking-widest mt-4 mb-2"
                style={{ color: '#C9A961' }}
              >
                {T.store_only_features_title.toUpperCase()}
              </div>
              <ul className="text-sm text-left max-w-sm mx-auto space-y-1.5 mt-3" style={{ color: '#4A5568' }}>
                {T.store_only_features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16A34A' }} />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* ========== BANDEAU 1 — HEBDOMADAIRE ========== */}
        {/* Apple Guideline 3.1.1 — masqué en PWA standalone iOS / TWA Play Store. */}
        {!installedApp && weeklyPlan && (
          <section className="mb-10" data-testid="pricing-section-weekly">
            <div
              className="rounded-2xl p-6 sm:p-7 border-2 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              style={{ borderColor: '#E5DCC9' }}
            >
              <div className="flex-1">
                <div className="text-[10px] font-bold tracking-widest mb-2" style={{ color: '#C9A961' }}>
                  {T.weekly_eyebrow}
                </div>
                <h3 className="text-xl font-bold mb-1" style={{ color: '#5A1620' }}>
                  {T.weekly_title}
                </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  {T.weekly_desc}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold" style={{ color: '#5A1620' }}>
                      {fmtPrice(weeklyPlan.amount)}
                    </span>
                    <span className="text-sm" style={{ color: '#4A5568' }}>{T.per_week_suffix}</span>
                  </div>
                  <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{T.taxes_excl}</p>
                </div>
                <button
                  onClick={() => startCheckout('weekly_solo')}
                  disabled={loading !== null || isActive}
                  className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-white transition disabled:opacity-50"
                  style={{ backgroundColor: '#7A1F2B' }}
                  data-testid="pricing-subscribe-weekly-solo-btn"
                >
                  {loading === 'weekly_solo' ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" /> {T.cta_redirecting}
                    </span>
                  ) : (
                    ctaLabel('weekly_solo')
                  )}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ========== BANDEAU 2+3 — MENSUEL / ANNUEL avec TOGGLE ========== */}
        {/* Apple Guideline 3.1.1 — masqué en PWA standalone iOS / TWA Play Store. */}
        {!installedApp && tiers.length > 0 && (
          <section className="mb-16" data-testid="pricing-section-subscription">
            {/* Section title + toggle */}
            <div className="text-center mb-7">
              <div className="text-xs font-bold tracking-widest mb-2" style={{ color: '#C9A961' }}>
                {T.sub_eyebrow}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-5" style={{ color: '#5A1620' }}>
                {T.sub_title}
              </h2>

              {/* Toggle Mensuel / Annuel */}
              <div
                className="inline-flex rounded-full p-1 border"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#E5DCC9' }}
                data-testid="pricing-billing-toggle"
              >
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className="px-5 py-2 rounded-full text-sm font-semibold transition"
                  style={
                    billingPeriod === 'monthly'
                      ? { backgroundColor: '#7A1F2B', color: '#FFFFFF' }
                      : { color: '#5A1620' }
                  }
                  data-testid="pricing-toggle-monthly"
                >
                  {T.toggle_monthly}
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className="px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition"
                  style={
                    billingPeriod === 'annual'
                      ? { backgroundColor: '#7A1F2B', color: '#FFFFFF' }
                      : { color: '#5A1620' }
                  }
                  data-testid="pricing-toggle-annual"
                >
                  {T.toggle_annual}
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                    style={{
                      backgroundColor: billingPeriod === 'annual' ? '#C9A961' : '#FAF7F2',
                      color: '#5A1620',
                    }}
                  >
                    {T.save_badge}
                  </span>
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {tiers.map((t) => {
                const plan = billingPeriod === 'annual' ? t.annual : t.monthly;
                const planKey = billingPeriod === 'annual' ? t.annualKey : t.monthlyKey;
                if (!plan) return null;
                const Icon = t.icon;
                const isPopular = t.accent;

                return (
                  <div
                    key={t.tier}
                    className="rounded-2xl p-6 relative transition"
                    style={
                      isPopular
                        ? { backgroundColor: '#7A1F2B', color: '#FAF7F2', boxShadow: '0 8px 30px rgba(122,31,43,0.20)', transform: 'translateY(-6px)' }
                        : { backgroundColor: '#FFFFFF', color: '#5A1620', border: '2px solid #E5DCC9' }
                    }
                    data-testid={`pricing-tier-${t.tier}`}
                  >
                    {isPopular && (
                      <div
                        className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest px-3 py-1 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
                      >
                        {T.most_popular}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mb-2 mt-1">
                      <Icon className="w-4 h-4" style={{ color: '#C9A961' }} />
                      <span
                        className="text-xs font-bold tracking-widest"
                        style={{ color: '#C9A961' }}
                      >
                        {t.label.toUpperCase()}
                      </span>
                    </div>

                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-4xl sm:text-5xl font-bold">
                        {fmtPrice(billingPeriod === 'annual' ? plan.monthly_equivalent : plan.amount)}
                      </span>
                      <span className="text-lg opacity-80">{T.per_month_suffix}</span>
                    </div>

                    <p className="text-xs mb-1" style={{ color: isPopular ? '#FFFFFFAA' : '#6B7280' }}>
                      {billingPeriod === 'annual'
                        ? T.billed_annually(fmtPrice(plan.amount))
                        : T.billed_monthly}
                    </p>

                    {billingPeriod === 'annual' && plan.savings ? (
                      <div
                        className="inline-block mt-2 text-[11px] font-bold px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: isPopular ? '#C9A96130' : '#FAF7F2',
                          color: isPopular ? '#C9A961' : '#7A1F2B',
                        }}
                      >
                        {T.savings_label(fmtPrice(plan.savings))}
                      </div>
                    ) : (
                      <div className="h-7" />
                    )}

                    <div
                      className="inline-flex items-center gap-1.5 text-xs font-semibold mt-4 mb-5 px-3 py-1.5 rounded-full"
                      style={{
                        backgroundColor: isPopular ? '#5A162050' : '#FAF7F2',
                        color: isPopular ? '#C9A961' : '#7A1F2B',
                        border: isPopular ? '1px solid #C9A96155' : 'none',
                      }}
                    >
                      <Users className="w-3 h-3" />
                      {t.seats === 1 ? T.seats_one : T.seats_up_to(t.seats)}
                    </div>

                    <button
                      onClick={() => startCheckout(planKey)}
                      disabled={loading !== null || isActive}
                      className="w-full py-3.5 rounded-xl font-semibold transition disabled:opacity-50"
                      style={
                        isPopular
                          ? { backgroundColor: '#C9A961', color: '#5A1620' }
                          : { backgroundColor: '#7A1F2B', color: '#FFFFFF' }
                      }
                      data-testid={`pricing-subscribe-${planKey}-btn`}
                    >
                      {loading === planKey ? (
                        <span className="inline-flex items-center gap-2 justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" /> {T.cta_redirecting}
                        </span>
                      ) : (
                        ctaLabel(planKey)
                      )}
                    </button>

                    <p className="text-xs opacity-70 text-center mt-3">{t.description}</p>

                    <ul className="mt-6 space-y-2.5">
                      {T.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px]" style={{ color: isPopular ? '#FAF7F2' : '#374151' }}>
                          <Check
                            className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                            style={{ color: isPopular ? '#C9A961' : '#16A34A' }}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* iOS native — Bouton "Restaurer mes achats" (exigé Apple Guideline 3.1.1)
            + disclaimer "Abonnements gérés par Apple". */}
        {nativeIOS && (
          <div className="mb-10" data-testid="pricing-ios-section">
            <div
              className="rounded-2xl p-5 sm:p-6 border text-center"
              style={{ backgroundColor: '#FFFFFF', borderColor: '#E5DCC9' }}
            >
              <p className="text-sm mb-4" style={{ color: '#4A5568' }}>
                {T.ios_pricing_note}
              </p>
              <button
                onClick={handleRestore}
                disabled={loading !== null}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition disabled:opacity-50"
                style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B', border: '1px solid #C9A961' }}
                data-testid="pricing-restore-purchases-btn"
              >
                {loading === '__restore__' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> {T.restoring}
                  </>
                ) : (
                  T.restore_purchases
                )}
              </button>
            </div>
          </div>
        )}

        {/* 🍎 SUBSCRIPTION TERMS — REQUIS Apple Guideline 3.1.2(c)
            Bloc affichant: nom/durée/prix déjà au-dessus dans les cartes,
            + texte auto-renew + texte "charged to Apple ID" + lien iOS Settings
            + liens FONCTIONNELS Privacy Policy + Terms of Use (EULA).
            Affiché TOUJOURS (iOS natif + web) pour conformité maximale. */}
        <div
          className="mb-10 rounded-2xl p-6 sm:p-7 border"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E5DCC9' }}
          data-testid="subscription-terms-block"
        >
          <h3
            className="font-bold text-base mb-3 flex items-center gap-2"
            style={{ color: '#5A1620' }}
          >
            <ShieldCheck className="w-5 h-5" style={{ color: '#C9A961' }} />
            {T.subscription_terms_title}
          </h3>
          <ul className="space-y-2 mb-5 text-sm leading-relaxed" style={{ color: '#4A5568' }}>
            {T.subscription_terms_bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="select-none" style={{ color: '#C9A961' }}>•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div
            className="pt-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            style={{ borderColor: '#E5DCC9' }}
          >
            <p className="text-xs" style={{ color: '#6B7280' }}>
              {T.legal_links_intro}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
              <a
                href="/privacy"
                onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
                className="underline underline-offset-4"
                style={{ color: '#7A1F2B' }}
                data-testid="pricing-privacy-policy-link"
              >
                {T.privacy_policy_link}
              </a>
              <a
                href="/terms"
                onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
                className="underline underline-offset-4"
                style={{ color: '#7A1F2B' }}
                data-testid="pricing-terms-of-use-link"
              >
                {T.terms_of_use_link}
              </a>
            </div>
          </div>
        </div>


        {/* GAME CHANGER — Prix fournisseurs.
            Apple Guideline 3.1.1 — masqué en PWA standalone iOS / TWA Play Store
            car le bloc parle d'inclusion dans l'abonnement payant. */}
        {!installedApp && (
          <div className="mt-4">
            <div
              className="relative overflow-hidden rounded-3xl p-6 sm:p-10 border-2"
              style={{ backgroundColor: '#5A1620', borderColor: '#C9A961' }}
              data-testid="pricing-suppliers-feature"
            >
            <div className="relative z-10">
              <div
                className="inline-flex items-center gap-2 text-xs font-bold tracking-widest mb-3 px-3 py-1 rounded-full"
                style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
              >
                <Sparkles className="w-3 h-3" /> {T.game_eyebrow}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight" style={{ color: '#FAF7F2' }}>
                {T.game_title_pre}<br />
                <span style={{ color: '#C9A961' }}>{T.game_title_emph}</span>
              </h2>
              <p className="text-base sm:text-lg leading-relaxed mb-6 max-w-2xl" style={{ color: '#E5DCC9' }}>
                {T.game_desc_pre}<strong style={{ color: '#FFF' }}>{T.game_desc_emph}</strong>{T.game_desc_post}
              </p>

              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                <div className="rounded-xl p-4" style={{ backgroundColor: '#7A1F2B', border: '1px solid #C9A96155' }}>
                  <FileSpreadsheet className="w-7 h-7 mb-2" style={{ color: '#C9A961' }} />
                  <div className="font-bold text-sm mb-1" style={{ color: '#FAF7F2' }}>{T.game_excel_title}</div>
                  <div className="text-xs leading-relaxed" style={{ color: '#E5DCC9' }}>
                    {T.game_excel_desc}
                  </div>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: '#7A1F2B', border: '1px solid #C9A96155' }}>
                  <Upload className="w-7 h-7 mb-2" style={{ color: '#C9A961' }} />
                  <div className="font-bold text-sm mb-1" style={{ color: '#FAF7F2' }}>{T.game_pdf_title}</div>
                  <div className="text-xs leading-relaxed" style={{ color: '#E5DCC9' }}>
                    {T.game_pdf_desc}
                  </div>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: '#7A1F2B', border: '1px solid #C9A96155' }}>
                  <Camera className="w-7 h-7 mb-2" style={{ color: '#C9A961' }} />
                  <div className="font-bold text-sm mb-1" style={{ color: '#FAF7F2' }}>{T.game_photo_title}</div>
                  <div className="text-xs leading-relaxed" style={{ color: '#E5DCC9' }}>
                    {T.game_photo_desc}
                  </div>
                </div>
              </div>

              <div
                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
                style={{ backgroundColor: '#C9A96120', color: '#FAF7F2', border: '1px solid #C9A96155' }}
              >
                <Truck className="w-3.5 h-3.5" />
                {T.game_footer}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* FAQ — masqué dans les apps de magasin car les questions parlent
            de cartes de crédit, facturation, abonnements (Guideline 3.1.1).
            🍎 Apple 2026: aussi masqué sur Capacitor iOS natif car les
            réponses mentionnent "carte de crédit" et "annuler depuis le profil"
            ce qui contredit le flow IAP / iOS Settings exigé par Apple. */}
        {!installedApp && !nativeIOS && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8" style={{ color: '#5A1620' }}>
            {T.faq_title}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {T.faqs.map((f) => (
              <div key={f.q} className="bg-white rounded-xl p-5 border" style={{ borderColor: '#E5DCC9' }}>
                <div className="font-semibold mb-2 text-sm" style={{ color: '#5A1620' }}>{f.q}</div>
                <div className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>{f.a}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        <div className="text-center mt-12 text-xs" style={{ color: '#6B7280' }}>
          {nativeIOS
            ? (lang === 'en'
                ? 'Subscriptions billed by Apple · Cancel anytime in iOS Settings · Support: support@soumissionpro.net'
                : 'Abonnements facturés par Apple · Annule à tout moment dans les Réglages iOS · Support : support@soumissionpro.net')
            : T.footer}
        </div>
      </main>
    </div>
  );
}
